import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { strings, SUPPORTED_LOCALES, DEFAULT_LOCALE } from "../i18n/strings";

const LocaleContext = createContext(null);

export function localeFromPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0];
  return SUPPORTED_LOCALES.includes(seg) ? seg : DEFAULT_LOCALE;
}

export function LocaleProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = localeFromPath(location.pathname);
  const [config, setConfig] = useState(null);
  const [alternates, setAlternates] = useState(null);

  useEffect(() => {
    let active = true;
    api.get(`/api/public/config?locale=${locale}`).then(({ data }) => {
      if (active) setConfig(data);
    }).catch(() => {});
    return () => { active = false; };
  }, [locale]);

  // Page-level localized URL alternates (set by detail pages). Cleared on navigation.
  useEffect(() => { setAlternates(null); }, [location.pathname]);
  const setPageAlternates = useCallback((map) => setAlternates(map), []);

  const changeLocale = useCallback((next) => {
    if (alternates && alternates[next]) {
      navigate(alternates[next] + location.search);
      return;
    }
    const parts = location.pathname.split("/").filter(Boolean);
    if (SUPPORTED_LOCALES.includes(parts[0])) parts[0] = next;
    else parts.unshift(next);
    navigate("/" + parts.join("/") + location.search);
  }, [location, navigate, alternates]);

  const t = useCallback((key) => strings[locale]?.[key] ?? strings.en[key] ?? key, [locale]);
  const lp = useCallback((path = "") => `/${locale}${path ? (path.startsWith("/") ? path : "/" + path) : ""}`, [locale]);

  const sections = config?.sections ?? [];
  const bySlug = Object.fromEntries(sections.map((s) => [s.slug, s]));
  // localized section path for the current locale (falls back to canonical slug)
  const sp = useCallback((slug) => (bySlug[slug]?.paths?.[locale]) || slug, [bySlug, locale]);
  // resolve an incoming localized path segment back to the canonical section slug
  const resolveSection = useCallback((pathParam) => {
    const hit = sections.find((s) => Object.values(s.paths || {}).includes(pathParam) || s.slug === pathParam);
    return hit ? hit.slug : pathParam;
  }, [sections]);

  const value = {
    locale,
    config,
    sections,
    locales: config?.locales ?? [],
    changeLocale,
    setPageAlternates,
    sp,
    resolveSection,
    t,
    lp,
  };
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}
